/**
 * Client-side wrapper for the Cloudflare Worker proxy.
 * The actual intervals.icu API key lives in Cloudflare Secrets and
 * never appears in the browser bundle.
 *
 * Set REACT_APP_PROXY_URL in .env.local for local dev (default: http://localhost:8787)
 */
import firebase from "firebase/app";
import "firebase/auth";

const PROXY =
  process.env.REACT_APP_PROXY_URL
    ?? "https://intervals-proxy.sebastiangranlund.workers.dev";

// In-memory credentials set after loading from Firestore
let _creds: { apiKey: string; athleteId: string } | null = null;

export function setIntervalsCreds(apiKey: string, athleteId: string) {
  _creds = { apiKey, athleteId };
}

export function clearIntervalsCreds() {
  _creds = null;
}

export function hasIntervalsCreds(): boolean {
  return _creds !== null;
}

async function proxyFetch(path: string, options: RequestInit = {}): Promise<Response> {
  if (!_creds) throw new Error("Intervals credentials not configured");
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Not authenticated");

  const token = await user.getIdToken();
  // Abort a request that hangs so a single stuck call can't freeze publishing.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  let res: Response;
  try {
    res = await fetch(`${PROXY}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        ...(options.headers ?? {}),
        Authorization: `Bearer ${token}`,
        "X-Intervals-Api-Key": _creds.apiKey,
        "X-Intervals-Athlete-Id": _creds.athleteId,
      },
    });
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Proxy error ${res.status}: ${body}`);
  }
  return res;
}

export interface TrainingPayload {
  wellness: unknown[];
  activities: unknown[];
  events: unknown[];
}

export async function fetchTrainingData(): Promise<TrainingPayload> {
  const res = await proxyFetch("/data");
  return res.json();
}

export interface HistoryPayload {
  wellness: unknown[];
  activities: unknown[];
}

export async function fetchTrainingHistory(years = 3): Promise<HistoryPayload> {
  const res = await proxyFetch(`/history?years=${years}`);
  return res.json();
}

export async function deleteTrainingEvent(eventId: number): Promise<void> {
  await proxyFetch(`/events/${eventId}`, { method: "DELETE" });
}

export async function updateTrainingEvent(
  eventId: number,
  startDateLocal: string   // "YYYY-MM-DDTHH:mm:ss" — date changed, time preserved
): Promise<void> {
  await proxyFetch(`/events/${eventId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ start_date_local: startDateLocal }),
  });
}

export async function updateEventDescription(
  eventId: number,
  description: string,
): Promise<void> {
  await proxyFetch(`/events/${eventId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description }),
  });
}

/** Update a race event's priority category on intervals.icu ("RACE_A" | "RACE_B" | "RACE_C"). */
export async function updateEventCategory(
  eventId: number,
  category: string,
): Promise<void> {
  await proxyFetch(`/events/${eventId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ category }),
  });
}

export async function postActivityMessage(
  activityId: string,
  content: string
): Promise<void> {
  await proxyFetch(`/activities/${activityId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
  });
}

export interface CreateEventPayload {
  start_date_local: string;
  name: string;
  type: string;
  description: string;
  category: string;
}

export async function createTrainingEvent(payload: CreateEventPayload): Promise<number> {
  const res = await proxyFetch("/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  return data.id;
}

export async function createTrainingEvents(
  payloads: CreateEventPayload[],
  onProgress?: (done: number, total: number) => void
): Promise<number[]> {
  const ids: number[] = [];
  for (let i = 0; i < payloads.length; i++) {
    ids.push(await createTrainingEvent(payloads[i]));
    onProgress?.(i + 1, payloads.length);
  }
  return ids;
}
